import React, { useEffect, useMemo, useState } from 'react';
import { Text, Platform, TextInput, View } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import * as XLSX from 'xlsx-js-style';

import { getForecastFilesData, getUniqueFilenames, getSheetsByFilename, getForecastingData } from '../../../api/AllAddress ';
import { BasicLayout, CommonContainer } from '../../../common/components/CustomLayout';
import { BOHTBody, BOHTD, BOHTDPressable, BOHTH2, BOHTHead, BOHTR, BOHTable } from '../../../common/components/bohtable';
import { msgStr } from '../../../common/constants/Message';
import { useAlertModal } from '../../../common/hooks';
import { BOHButton, BOHTlbCheckbox, BOHTlbrSearchPicker, BOHToolbar, renderBOHTlbDatePicker } from '../../../common/components/bohtoolbar';
import { TextdefaultSize } from '../../../common/constants/Fonts';
import { useSorting } from '../../../common/hooks/useSorting';
import UploadXlsModal from './UploadXlsModal';

const CompareForecast = ({ navigation }) => {
  const { showAlert } = useAlertModal();

  const [tableData, setTableData] = useState([]);
  const [daysArray, setdaysArray] = useState([]);
  const [originalTableData, setOriginalTableData] = useState([]); 
  const [totalNights, setTotalNights] = useState(0);
  const [totalSystemNights, setTotalSystemNights] = useState(0);
  const [updateLocationTrigger, setUpdateLocationsTrigger] = useState(false);
  const InitialWidths = [100, 160, 160, 120, 120, 140, 140, 140, 140, 120, 120, 150, 150];
  const [isLoading, setLoading] = useState(false);

  const [filenames, setFilenames] = useState([]);
  const [sheetNames, setSheetNames] = useState([]);
  const [isUploadXlsModalVisible, setUploadXlsModalVisible] = useState(false);

  const [isManualAddress, setIsManualAddress] = useState(false);
  const [searchInput, setSearchInput] = useState('');

  // Get current month's start and end dates
  const getCurrentMonthDates = () => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    
    // Format dates as YYYY-MM-DD
    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    
    return {
      startDate: formatDate(firstDay),
      endDate: formatDate(lastDay)
    };
  };

  const { startDate, endDate } = getCurrentMonthDates();

  const [searchOptions, setSearchOptions] = useState({
    start_date: startDate,
    end_date: endDate,
    pms: '',
    pmc: '',
    program: '',
    address: '',
    status: '',
    confirmation_id: '',
    filename: '',
    sheet_name: '',
    searchKey: '',
    nights_filter: '',
    isGroupedByAddress: true,
    isXploriefif: false,
    isVoucher: false,
  });  

  // Filter options for nights comparison
  const nightsFilterOptions = [
    { label: 'All Records', value: '' },
    { label: 'Mismatched Only (Red)', value: 'mismatched' },
    { label: 'Matched Only (Green)', value: 'matched' },
    { label: 'Zero Nights with System Nights (Yellow)', value: 'zero_with_system' },
  ];

  const changeSearchOptions = (key: string, val: any) => {
    setSearchOptions(prevOptions => {
      const newOptions = {
        ...prevOptions,
        [key]: val
      };
      
      // Reset sheet_name when filename changes
      if (key === 'filename') {
        newOptions.sheet_name = '';
        // Load sheets for the selected filename
        if (val) {
          loadSheetsByFilename(val);
        } else {
          setSheetNames([]);
        }
      }
      
      return newOptions;
    });
  }

  // Load unique filenames on component mount
  const loadFilenames = () => {
    getUniqueFilenames((jsonRes, status, error) => {
      switch (status) {
        case 200:
          const fileList = jsonRes.data || [];
          setFilenames(fileList);
  
          // Auto-select the first filename if not already selected
          // if (fileList.length > 0 && !searchOptions.filename) {
            // changeSearchOptions('filename', fileList[0]);
          // }
          break;
        case 500:
          showAlert('error', msgStr('serverError'));
          break;
        default:
          if (jsonRes && jsonRes.error) showAlert('error', jsonRes.error);
          else showAlert('error', 'Failed to load filenames');
          break;
      }
    });
  };

  // Load sheet names for selected filename
  const loadSheetsByFilename = (filename) => {
    if (!filename) {
      setSheetNames([]);
      return;
    }

    getSheetsByFilename({ filename }, (jsonRes, status, error) => {
      switch (status) {
        case 200:
          setSheetNames(jsonRes.data || []);
          break;
        case 500:
          showAlert('error', msgStr('serverError'));
          break;
        default:
          if (jsonRes && jsonRes.error) showAlert('error', jsonRes.error);
          else showAlert('error', 'Failed to load sheet names');
          break;
      }
    });
  };

  const openUploadXlsModal = () => {
    setUploadXlsModalVisible(true);
  };
  
  const closeUploadXlsModal = () => {
    setUploadXlsModalVisible(false);
  };

  const handleFileUploaded = (parsedData, fileInfo) => {    
    showAlert('success', `File "${fileInfo.name}" uploaded successfully`);
    // Refresh the table data and filenames after upload
    setUpdateLocationsTrigger(true);
    loadFilenames(); // Reload filenames as new file might be added
  };

  // Debounce search input - only trigger API call after user stops typing for 500ms
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (searchInput !== searchOptions.searchKey) {
        setSearchOptions(prev => ({ ...prev, searchKey: searchInput }));
      }
    }, 500);

    return () => clearTimeout(debounceTimer);
  }, [searchInput]);

  useEffect(() => {
    setLoading(true);
    setUpdateLocationsTrigger(true);
  }, [searchOptions])

  useEffect(() => {
    if (updateLocationTrigger == true) getTable();
  }, [updateLocationTrigger]);

  // Load filenames on component mount
  useEffect(() => {
    loadFilenames();
  }, []);

  const getTable = () => {
    setLoading(true);
    // You'll need to create this API function to fetch ForecastFiles data
    getForecastFilesData({ searchOptions }, (jsonRes, status, error) => {
      switch (status) {
        case 200:
          setUpdateLocationsTrigger(false);
          // Handle null or empty data gracefully
          if (jsonRes.data && Array.isArray(jsonRes.data)) {
            setTableData(jsonRes.data);
            setOriginalTableData(jsonRes.data);
          } else {
            setTableData([]);
            setOriginalTableData([]);
          }
          // Handle daysArray
          if (jsonRes.daysArray && Array.isArray(jsonRes.daysArray)) {
            setdaysArray(jsonRes.daysArray);
          } else {
            setdaysArray([]);
          }
          break;
        case 500:
          showAlert('error', msgStr('serverError'));
          setTableData([]);
          setdaysArray([]);
          break;
        default:
          if (jsonRes && jsonRes.error) showAlert('error', jsonRes.error);
          else showAlert('error', msgStr('unknownError'));
          setTableData([]);
          setdaysArray([]);
          break;
      }
      setLoading(false);
    });
  };

  const { sortedData, handleSort, sorting } = useSorting('forecast_files', tableData, { key: 'excel_nights', direction: 'descending' });

  const exportToExcel = () => {
    if (!filteredData || filteredData.length === 0) {
      showAlert('error', 'No data to export');
      return;
    }

    try {
      // Prepare headers
      const headers = [
        'PMS',
        'PMC',
        'Program',
        'Unit',
        'Arrival Date',
        'Departure Date',
        'Confirmation ID',
        'Total Nights',
        'System Nights',
        'Status',
        'Booking Date',
        'File Name',
        'Sheet Name',
        ...daysArray.map(day => day.formattedDate)
      ];

      // Prepare data rows with color information
      const dataRows = filteredData.map(item => {
        const totalNights = parseInt(item.excel_nights) || 0;
        const forecastDates = item.forecast_dates ? item.forecast_dates.split(',') : [];
        const systemNights = forecastDates.length > 0 ? forecastDates.length - 1 : 0;

        // Determine row background color
        let rowColor = null;
        if (searchOptions.filename && searchOptions.filename.trim() !== '') {
          if (totalNights === 0 && systemNights > 0) {
            rowColor = 'FEFF03'; // Yellow
          } else if (totalNights !== systemNights) {
            rowColor = 'FFCDD2'; // Red
          }
        }

        // Base row data
        const rowData = {
          values: [
            item.pms || '',
            item.pmc || '',
            item.program || '',
            item.address || '',
            item.arrival_date || '',
            item.departure_date || '',
            item.confirmation_id || '',
            totalNights,
            systemNights,
            item.status || '',
            item.booking_date || '',
            item.file_name || '',
            item.sheet_name || ''
          ],
          rowColor: rowColor,
          cellColors: []
        };

        // Add day columns with their colors
        daysArray.forEach(day => {
          const forecastDatesSorted = forecastDates
            .map(date => date.trim())
            .sort((a, b) => new Date(a).getTime() - new Date(b).getTime());

          let cellValue = '';
          let cellColor = null;
          
          // Parse price field
          let priceValue = '';
          let priceRanges = [];
          if (item.price) {
            const matches = item.price.matchAll(/(\d+),(\d{4}-\d{2}-\d{2})\s+to\s+(\d{4}-\d{2}-\d{2})\s*,\s*\$?([\d.]+)/g);
            for (const match of matches) {
              priceRanges.push({
                id: match[1],
                startDate: match[2],
                endDate: match[3],
                price: parseFloat(match[4]).toFixed(2)
              });
            }
          }

          const matchingPrice = priceRanges.find(range => 
            day.date >= range.startDate && day.date <= range.endDate
          );
          if (matchingPrice) {
            priceValue = `$${matchingPrice.price}`;
          }

          // Only apply color highlighting if a filename is selected
          if (searchOptions.filename && searchOptions.filename.trim() !== '') {
            if (item.pairs) {
              const pairsRanges = item.pairs.split(', ').map(range => {
                const [start, end] = range.split('→');
                return { start, end };
              });
              const isInPairsRange = pairsRanges.some(range => day.date >= range.start && day.date < range.end);
              const validDisplayDates = forecastDatesSorted.slice(0, systemNights);
              const isForecastDay = validDisplayDates.includes(day.date);

              if (isInPairsRange && isForecastDay) {
                // In pairs and in forecast: Green
                cellColor = '04D10A';
                cellValue = priceValue || '1';
              } else if (isInPairsRange && !isForecastDay) {
                // In pairs but NOT in forecast: Red
                cellColor = 'D7061B';
                cellValue = priceValue || '1';
              } else if (!isInPairsRange && isForecastDay) {
                // Not in pairs but in forecast: Yellow
                cellColor = 'F9EB25';
                cellValue = priceValue || '1';
              } else {
                cellValue = priceValue || '';
              }
            } else {
              // When pairs is null, only show yellow for forecast dates within systemNights
              const validDisplayDates = forecastDatesSorted.slice(0, systemNights);
              const isForecastDay = validDisplayDates.includes(day.date);

              if (isForecastDay) {
                cellColor = 'F9EB25'; // Yellow
                cellValue = priceValue || '1';
              } else {
                cellValue = priceValue || '';
              }
            }
          } else {
            cellValue = priceValue || '';
          }

          rowData.values.push(cellValue);
          rowData.cellColors.push(cellColor);
        });

        return rowData;
      });


      // Create workbook
      const wb = XLSX.utils.book_new();
      
      // Check column count before processing
      const totalColumns = headers.length;
      const maxExcelColumns = 16384; // Excel 2007+ limit
      
      if (totalColumns > maxExcelColumns) {
        showAlert('error', `Too many columns (${totalColumns}). Excel limit is ${maxExcelColumns}.`);
        return;
      }
      
      console.log(`Exporting ${totalColumns} columns (${totalColumns - 13} date columns + 13 static columns)`);
      
      // Create worksheet data
      const wsData = [
        headers,
        ...dataRows.map(row => row.values)
      ];

      // Create worksheet
      const ws = XLSX.utils.aoa_to_sheet(wsData);

      // Set column widths
      const colWidths = [
        { wch: 10 },  // PMS
        { wch: 15 },  // PMC
        { wch: 15 },  // Program
        { wch: 20 },  // Unit
        { wch: 12 },  // Arrival Date
        { wch: 12 },  // Departure Date
        { wch: 15 },  // Confirmation ID
        { wch: 12 },  // Total Nights
        { wch: 12 },  // System Nights
        { wch: 12 },  // Status
        { wch: 12 },  // Booking Date
        { wch: 20 },  // File Name
        { wch: 20 },  // Sheet Name
        ...daysArray.map(() => ({ wch: 10 })) // Day columns
      ];
      ws['!cols'] = colWidths;

      // Apply styling
      const range = XLSX.utils.decode_range(ws['!ref']);
      
      // Style header row
      for (let C = range.s.c; C <= range.e.c; ++C) {
        const address = XLSX.utils.encode_col(C) + "1";
        if (!ws[address]) continue;
        ws[address].s = {
          font: { bold: true, color: { rgb: "FFFFFF" } },
          fill: { fgColor: { rgb: "4472C4" } },
          alignment: { horizontal: "center", vertical: "center" }
        };
      }

      // Style data rows
      dataRows.forEach((rowData, rowIndex) => {
        const excelRowNum = rowIndex + 2; // +2 because Excel is 1-indexed and we have header
        
        // Apply row background color if exists
        if (rowData.rowColor) {
          for (let C = range.s.c; C <= range.e.c; ++C) {
            const address = XLSX.utils.encode_col(C) + excelRowNum;
            if (!ws[address]) ws[address] = { t: 's', v: '' };
            ws[address].s = {
              fill: { fgColor: { rgb: rowData.rowColor } },
              alignment: { vertical: "center" }
            };
          }
        }

        // Apply individual cell colors for day columns
        const dayColumnStartIndex = 13; // Days start after the 13 static columns
        rowData.cellColors.forEach((cellColor, cellIndex) => {
          const C = dayColumnStartIndex + cellIndex;
          const address = XLSX.utils.encode_col(C) + excelRowNum;
          const cellValue = rowData.values[C];
          
          if (!ws[address]) ws[address] = { t: 's', v: '' };
          
          const cellStyle: any = {
            alignment: { horizontal: "center", vertical: "center" },
            font: { 
              bold: true,
              color: { rgb: (cellColor === "D7061B" && cellValue && cellValue.startsWith('$')) ? "FFFFFF" : (cellValue && cellValue.startsWith('$')) ? "D7061B" : "000000" }
            }
          };
          
          // Only apply fill color if cellColor exists
          if (cellColor) {
            cellStyle.fill = { fgColor: { rgb: cellColor } };
          }
          
          ws[address].s = cellStyle;
        });
      });


      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(wb, ws, "Compare Forecast");

      // Generate filename
      const timestamp = new Date().toISOString().split('T')[0];
      const filters = [];
      if (searchOptions.filename) filters.push(searchOptions.filename.replace(/[^a-z0-9]/gi, '_'));
      if (searchOptions.sheet_name) filters.push(searchOptions.sheet_name.replace(/[^a-z0-9]/gi, '_'));
      if (searchOptions.searchKey) filters.push('filtered');
      
      const filterSuffix = filters.length > 0 ? `_${filters.join('_')}` : '';
      const filename = `compare_forecast${filterSuffix}_${timestamp}.xlsx`;

      // Write file
      if (Platform.OS === 'web') {
        XLSX.writeFile(wb, filename);
        showAlert('success', `Exported ${filteredData.length} records to ${filename}`);
      } else {
        showAlert('info', 'Export is only available on web platform');
      }
    } catch (error) {
      console.error('Export error:', error);
      showAlert('error', 'Failed to export data: ' + error.message);
    }
  };

  const openReservations = (searchOptions) => {
    AsyncStorage.setItem('__potential_options', JSON.stringify(searchOptions));

    if (Platform.OS == 'web') {
      window.open('/home/PotentialList');
    }
  };

  // Filter data based on nights comparison and search key
  const filteredData = useMemo(() => {
    if (!sortedData) {
      return [];
    }
    
    // Apply only the search key filter if it exists
    let filtered = sortedData;
    
    if (searchOptions.searchKey && searchOptions.searchKey.trim() !== '') {
      const searchTerm = searchOptions.searchKey.toLowerCase().trim();
      filtered = filtered.filter((item) => {
        // Search in all text fields
        return Object.keys(item).some(key => {
          const value = item[key];
          return value && typeof value === 'string' && value.toLowerCase().includes(searchTerm);
        });
      });
    }
    
    return filtered;
  }, [sortedData, searchOptions.searchKey]);

  // Calculate totals based on filtered data
  useEffect(() => {
    // Only calculate if filteredData exists and has items
    if (filteredData && Array.isArray(filteredData) && filteredData.length > 0) {
      const calculatedTotalNights = filteredData.reduce((sum, item) => sum + (parseInt(item.excel_nights) || 0), 0);
      const calculatedSystemNights = filteredData.reduce((sum, item) => {
        const forecastDates = item.forecast_dates ? item.forecast_dates.split(',') : [];
        return sum + (forecastDates.length > 0 ? forecastDates.length - 1 : 0);
      }, 0);
      
      setTotalNights(calculatedTotalNights);
      setTotalSystemNights(calculatedSystemNights);
    } else if (filteredData && Array.isArray(filteredData) && filteredData.length === 0) {
      // If filteredData is an empty array, set totals to 0
      setTotalNights(0);
      setTotalSystemNights(0);
    }
    // If filteredData is undefined or null, don't update the totals
  }, [filteredData]);

  const renderTableData = () => {
    if (filteredData && filteredData.length > 0) {
      return filteredData.map((item, index) => {
        const totalNights = parseInt(item.excel_nights) || 0;

        // Calculate systemNights from the forecast_dates count
        const forecastDates = item.forecast_dates
          ? item.forecast_dates.split(',')
          : [];
        const systemNights = forecastDates.length > 0 ? forecastDates.length - 1 : 0;
  
        let rowStyle = {};
        // Only apply row background colors if a filename is selected
        if (searchOptions.filename && searchOptions.filename.trim() !== '') {
          if (totalNights === 0 && systemNights > 0) {
            rowStyle = { backgroundColor: '#FEFF03' }; // Yellow when total nights is 0 and system nights > 0
          } else if (totalNights !== systemNights) {
            rowStyle = { backgroundColor: '#ffcdd2' }; // Red for all other mismatches
          }
        }
  
        return (
          <BOHTR key={index} style={rowStyle}>
            <BOHTD width={InitialWidths[0]}>{item.pms || " "}</BOHTD>
            <BOHTD width={InitialWidths[1]}>{item.pmc || " "}</BOHTD>
            <BOHTD width={InitialWidths[2]}>{item.program || " "}</BOHTD>
            <BOHTD width={InitialWidths[3]}>{item.address || " "}</BOHTD>
            <BOHTD width={InitialWidths[4]}>{item.arrival_date}</BOHTD>
            <BOHTD width={InitialWidths[5]}>{item.departure_date}</BOHTD>
            <BOHTD width={InitialWidths[6]}>{item.confirmation_id || " "}</BOHTD>
            <BOHTD width={InitialWidths[7]} textAlign={'center'}>{item.excel_nights || "-"}</BOHTD>
            <BOHTD width={InitialWidths[8]} textAlign={'center'}>{systemNights || "-"}</BOHTD>
            <BOHTD width={InitialWidths[9]}>{item.status || " "}</BOHTD>
            <BOHTD width={InitialWidths[10]}>{item.booking_date}</BOHTD>
            <BOHTD width={InitialWidths[11]}>{item.file_name || " "}</BOHTD>
            <BOHTD width={InitialWidths[12]}>{item.sheet_name || " "}</BOHTD>
            {daysArray.map((day, dIndex) => {
              const forecastDates = item.forecast_dates
                ? item.forecast_dates
                    .split(',')
                    .map((date: any) => date.trim())
                    .sort((a: any, b: any) => new Date(a).getTime() - new Date(b).getTime())
                : [];

              let forecastTxt = '#000';
              let isDayHighlighted = false;
              let isGreenSplit = false;
              let isRedSplit = false;
              let priceValue = '';

              // Parse price field to extract reservation ID, date range and price
              // Format: "6362,2025-03-08 to 2025-03-14 , $100.0000 | 6839,2025-03-22 to 2025-03-28 , $34.3000"
              let priceRanges = [];
              if (item.price) {
                // Match all patterns: "ID,YYYY-MM-DD to YYYY-MM-DD , $XX.XX"
                const matches = item.price.matchAll(/(\d+),(\d{4}-\d{2}-\d{2})\s+to\s+(\d{4}-\d{2}-\d{2})\s*,\s*\$?([\d.]+)/g);
                for (const match of matches) {
                  priceRanges.push({
                    id: match[1],
                    startDate: match[2],
                    endDate: match[3],
                    price: parseFloat(match[4]).toFixed(2)
                  });
                }
              }

              // Base style for the cell
              const cellStyle: any = {
                borderRightWidth: 1,
                borderRightColor: '#f2f2f2',
                borderLeftWidth: 1,
                borderLeftColor: '#f2f2f2',
                paddingHorizontal: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                backgroundColor: 'white' // Default
              };

              // Check if this date falls within a price range (independent of highlighting)
              const matchingPrice = priceRanges.find(range => 
                day.date >= range.startDate && day.date <= range.endDate
              );
              if (matchingPrice) {
                priceValue = matchingPrice.price;
              }

              // Only apply color highlighting if a filename is selected
              if (searchOptions.filename && searchOptions.filename.trim() !== '') {
                if (item.pairs) {
                  const pairsRanges = item.pairs.split(', ').map(range => {
                    const [start, end] = range.split('→');
                    return { start, end };
                  });
                  const isInPairsRange = pairsRanges.some(range => day.date >= range.start && day.date < range.end);
                  const validDisplayDates = forecastDates.slice(0, systemNights);
                  const isForecastDay = validDisplayDates.includes(day.date);

                  if (isInPairsRange && isForecastDay) {
                    // In pairs and in forecast: Split Green/Yellow
                    isGreenSplit = true;
                    isDayHighlighted = true;
                  } else if (isInPairsRange && !isForecastDay) {
                    // In pairs but NOT in forecast: Red Split
                    isRedSplit = true;
                    isDayHighlighted = true;
                  } else if (!isInPairsRange && isForecastDay) {
                    // Not in pairs but in forecast: Yellow
                    cellStyle.backgroundColor = '#f9eb25ff';
                    isDayHighlighted = true;
                  }
                } else {
                  // When pairs is null, only show yellow for forecast dates within systemNights
                  const validDisplayDates = forecastDates.slice(0, systemNights);
                  const isForecastDay = validDisplayDates.includes(day.date);

                  if (isForecastDay) {
                    cellStyle.backgroundColor = '#f9eb25ff'; // yellow
                    isDayHighlighted = true;
                  }
                }

                if (isGreenSplit) {
                  cellStyle.background = 'linear-gradient(to bottom, #f9eb25ff 50%, #04d10a8b 50%)';
                } else if (isRedSplit) {
                  cellStyle.background = 'linear-gradient(to bottom, white 50%, #d7061bff 50%)';
                }
              }

              const txtColor = forecastTxt;

              // Use BOHTDPressable for price cells to make them clickable
              if (priceValue) {
                return (
                  <BOHTDPressable
                    key={dIndex}
                    width={40}
                    style={cellStyle}
                    textProps={{
                      style: {
                        fontWeight: 'bold',
                        textAlign: 'center',
                        color: '#d7061bff',
                        fontSize: 9,
                      }
                    }}
                    onPress={() => {
                      openReservations({
                        date: day.date,
                        address: item.address ? item.address.replace(',', ' ') : '',
                        ids: matchingPrice?.id ? [matchingPrice.id] : null,
                        potential: priceValue,
                      });
                    }}
                  >
                    {`$${priceValue}`}
                  </BOHTDPressable>
                );
              }

              return (
                <BOHTD
                  key={dIndex}
                  width={40}
                  style={cellStyle}
                  textProps={{
                    style: {
                      fontWeight: isDayHighlighted ? 'bold' : 'normal',
                      textAlign: 'center',
                      color: txtColor,
                    }
                  }}
                >
                  {isDayHighlighted ? "1" : " "}
                </BOHTD>
              );
            })}
          </BOHTR>
        );
      });
    }

    return (
      <BOHTR>
        <BOHTD width={InitialWidths.reduce((sum, width) => sum + width, 0)} textAlign="center">
          No data found
        </BOHTD>
      </BOHTR>
    );
  };

  const headers = [
    { key: 'pms', label: 'PMS' },
    { key: 'pmc', label: 'PMC' },
    { key: 'program', label: 'Program' },
    { key: 'address', label: 'Unit' },
    { key: 'arrival_date', label: 'Arrival Date' },
    { key: 'departure_date', label: 'Departure Date' },
    { key: 'confirmation_id', label: 'Confirmation ID' },
    { key: 'excel_nights', label: 'Total Nights' },
    { key: 'system_nights', label: 'System Nights' },
    { key: 'reservation_status', label: 'Status' },
    { key: 'booking_date', label: 'Booking Date' },
    { key: 'file_name', label: 'File Name' },
    { key: 'sheet_name', label: 'Sheet Name' },
  ];

  const tableElement = useMemo(() => (
    <BOHTable>
      <BOHTHead>
        <BOHTR>
          {headers.map(({ key, label }, wIndex) => (
            <BOHTH2
              key={key}
              width={InitialWidths[wIndex]}
              isSorted={sorting.key === key}
              direction={sorting.key === key ? sorting.direction : null}
              onClick={() => handleSort(key)}
            >
              {label}
            </BOHTH2>
          ))}
          {daysArray.length > 0 && daysArray.map((item, index) => (
            <BOHTH2
              key={index}
              width={40}
              BoxStyle={{ height: 100 }}
              style={{
                whiteSpace: 'nowrap',
                padding: 0,
                marginTop: 10,
                transform: [{ rotate: '-90deg' }]
              }}
            >{item.formattedDate}</BOHTH2>
          ))}
        </BOHTR>
      </BOHTHead>
      <BOHTBody>
        {renderTableData()}
      </BOHTBody>
    </BOHTable>
  ), [filteredData]);

  return (
    <BasicLayout
      navigation={navigation}
      goBack={() => {
        navigation.navigate('Marketing');
      }}
      screenName={'Compare Forecast'}
      isLoading={isLoading}
    >
      <CommonContainer>
        <BOHToolbar style={{ zIndex: 10 }}>

          <BOHTlbrSearchPicker
            width={150}
            label="File Name"
            boxStyle={{ marginLeft: 0 }}
            items={[
              {
              label: "",
              value: ""
            },
            ...filenames.map((filename) => ({
              label: filename,
              value: filename
            }))
          ]}
            selectedValue={searchOptions.filename || ''}
            onValueChange={val => changeSearchOptions('filename', val)}
          />

          {/* Sheet Name Filter - dependent on filename */}
          <BOHTlbrSearchPicker
            width={150}
            label="Sheet Name"
            items={[
              { label: '', value: '' },
              ...sheetNames.map((sheetName) => ({
                label: sheetName,
                value: sheetName
              }))
            ]}
            selectedValue={searchOptions.sheet_name || ''}
            onValueChange={val => changeSearchOptions('sheet_name', val)}
          />

          {/* New Nights Comparison Filter */}
          <BOHTlbrSearchPicker
            width={180}
            label="Nights Filter"
            items={nightsFilterOptions}
            selectedValue={searchOptions.nights_filter || ''}
            onValueChange={val => changeSearchOptions('nights_filter', val)}
          /> 

          <Text style={{ fontSize: TextdefaultSize, marginLeft: 30, }}>{`Total Nights: ${totalNights}`}</Text>
          <Text style={{ fontSize: TextdefaultSize, marginLeft: 30, }}>{`Total System Nights: ${totalSystemNights}`}</Text>

          <BOHButton
            style={{ marginLeft: 30 }}
            label='Upload XLS'
            onPress={openUploadXlsModal}
          />

          <BOHButton
            style={{ marginLeft: 15 }}
            label='Export to Excel'
            onPress={exportToExcel}
          />

          <View style={{ flexDirection: 'column', marginLeft: 20 }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 6 }}>
              <View style={{ width: 25, height: 15, backgroundColor: '#ffef6c', borderWidth: 1, borderColor: '#ccc', marginRight: 8 }} />
              <Text style={{ fontSize: TextdefaultSize, fontFamily: 'Arial' }}>Night not listed on Xplorie's summary (excel file), but we show booked in our system (book in system VS not in excel)</Text>
            </View>
            <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 6 }}>
              <View style={{ width: 25, height: 15, backgroundColor: '#ff3b30', borderWidth: 1, borderColor: '#ccc', marginRight: 8 }} />
              <Text style={{ fontSize: TextdefaultSize, fontFamily: 'Arial' }}>Night listed on Xplorie summary(excel file), but  do not show booked in our system (not booked in system VS in excel)</Text>
            </View>
            <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 6 }}>
              <View style={{ width: 25, height: 15, backgroundColor: '#04d10a8b', borderWidth: 1, borderColor: '#ccc', marginRight: 8 }} />
              <Text style={{ fontSize: TextdefaultSize, fontFamily: 'Arial' }}>Night matching with Xplorie summary(excel file) and our system (book in system VS in excel)</Text>
            </View>
            {/* <View style={{ flexDirection: 'row', alignItems: 'center' }}>
              <View style={{ width: 25, height: 15, backgroundColor: '#e2dfdfff', borderWidth: 1, borderColor: '#ccc', marginRight: 8 }} />
              <Text style={{ fontSize: TextdefaultSize, fontFamily: 'Arial' }}>Number of Nights booked in our system</Text>
            </View> */}
          </View>
        </BOHToolbar>

        <BOHToolbar style={{ zIndex: 10 }}>
          <Text style={{ marginRight: 8, fontSize: TextdefaultSize, marginLeft: 0 }}>Start</Text>
          {Platform.OS == 'web' &&
            renderBOHTlbDatePicker(searchOptions.start_date, (date) => {
              const year = date.getFullYear();
              const formattedDate = `${year}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;
              changeSearchOptions('start_date', formattedDate);
            })}
          <Text style={{ marginHorizontal: 8, fontSize: TextdefaultSize }}>End</Text>
          {Platform.OS == 'web' &&
            renderBOHTlbDatePicker(searchOptions.end_date, (date) => {
              const year = date.getFullYear();
              const formattedDate = `${year}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;
              changeSearchOptions('end_date', formattedDate);
            })}

          <Text style={{ marginLeft: 30, marginRight: 8, fontSize: TextdefaultSize }}>Search</Text> 
          <TextInput
            placeholder=""
            value={searchInput}
            onChangeText={(text) => setSearchInput(text)}
            style={{
              height: 30,
              width: 200,
              paddingHorizontal: 10,
              borderColor: '#999999',
              borderWidth: 1,
              borderRadius: 3,
            }}
          />

          <BOHTlbCheckbox
            label={'Group by Address'}
            CheckboxProps={{ value: true }}
            onPress={() => {}} // This checkbox is now permanently checked
          />

          <BOHTlbCheckbox
            label={'xploriefif'}
            CheckboxProps={{ value: !!searchOptions.isXploriefif }}
            onPress={() => {
              changeSearchOptions('isXploriefif', !searchOptions.isXploriefif);
            }}
          />

          <BOHTlbCheckbox
            label={'voucher'}
            CheckboxProps={{ value: !!searchOptions.isVoucher }}
            onPress={() => {
              changeSearchOptions('isVoucher', !searchOptions.isVoucher);
            }}
          />

          <BOHTlbCheckbox
            label={'Show Only Manual Addresses'}
            CheckboxProps={{ value: !!isManualAddress }}
            onPress={()=>{            
              // Toggle between empty and original data
              if (!isManualAddress) {
                // Store current data if not already stored
                if (tableData.length > 0 && originalTableData.length === 0) {
                  setOriginalTableData(tableData);
                }

                // Empty the table and reset counts
                setTableData([]);
                setTotalNights(0);
                setTotalSystemNights(0);
                setIsManualAddress(true);
              } else {
                // Restore original data
                setTableData(originalTableData);
                setIsManualAddress(false);
                // The useEffect will recalculate the totals based on filteredData
              }
            }}
          />
        </BOHToolbar>

        {tableElement}
      </CommonContainer>
      <UploadXlsModal
        isModalVisible={isUploadXlsModalVisible}
        closeModal={closeUploadXlsModal}
        onFileUploaded={handleFileUploaded}
      />
    </BasicLayout>
  );
};

export default CompareForecast;